import { BookOpen, Inbox, type LucideIcon } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { IconCircle } from '../atoms/IconCircle'

/**
 * EmptyState — zero-data fallback. Pencil XDdD3.
 *
 * Layout is `flex flex-col items-center` with an explicit `w-full` on the
 * text block. Without the explicit width the flex child collapses to the
 * longest unbreakable token and renders every word on its own line.
 *
 * Inline-help integration (Vzk9b/nYOmw/PBfNT — S4 EmptyState Help CTA):
 * pass `helpTopicId` to auto-render a "Learn how X works" outline button
 * alongside the primary actions. The same prop is read by
 * `<InlineHelpProvider>` at app boot to pre-fetch the topic. A small
 * meta hint (`helpTopicId = …`) renders below the actions when the topic
 * is wired — a dev-facing affordance to make the cross-reference obvious
 * during page-by-page rollout. Disable with `hideHelpTopicMeta`.
 */
export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: LucideIcon
  title: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
  tone?: 'neutral' | 'brand' | 'info'
  /**
   * Inline-help topic id (e.g. `tenant_admin.applications.create`). When set, an
   * outline "Learn how X works" button renders to the left of `actions`
   * and a small mono-font footer hint shows the topic id.
   */
  helpTopicId?: string
  /** Override the default "Learn how X works" button label. */
  learnMoreLabel?: React.ReactNode
  /** Called when the user clicks the learn-more button. Usually opens the InlineHelpSheet. */
  onLearnMore?: () => void
  /** Hide the small `helpTopicId = …` footer hint. */
  hideHelpTopicMeta?: boolean
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      className,
      icon = Inbox,
      title,
      description,
      actions,
      tone = 'neutral',
      helpTopicId,
      learnMoreLabel = 'Show me how this works',
      onLearnMore,
      hideHelpTopicMeta,
      ...props
    },
    ref,
  ) => {
    const showLearnButton = Boolean(helpTopicId && onLearnMore)
    const showMeta = Boolean(helpTopicId) && !hideHelpTopicMeta
    return (
      <div
        ref={ref}
        className={cn(
          'w-full flex flex-col items-center justify-center text-center',
          'py-12 px-6 gap-4',
          className,
        )}
        {...props}
      >
        <IconCircle icon={icon} tone={tone} size="xl" />
        <div className="flex flex-col gap-1 w-full max-w-[480px]">
          <h3 className="text-heading-md text-ia-text">{title}</h3>
          {description && (
            <p className="text-body-sm text-ia-text-secondary leading-relaxed">{description}</p>
          )}
        </div>
        {(showLearnButton || actions) && (
          <div className="flex items-center gap-3 flex-wrap justify-center">
            {showLearnButton && (
              <button
                type="button"
                onClick={onLearnMore}
                data-help-topic-id={helpTopicId}
                className={cn(
                  'inline-flex items-center gap-2 h-10 px-[18px] rounded-md cursor-pointer',
                  'border border-ia-border-strong bg-transparent text-body-sm font-medium text-ia-text',
                  'hover:bg-ia-surface-hover transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ia-green',
                )}
              >
                <BookOpen size={15} className="text-ia-green" />
                {learnMoreLabel}
              </button>
            )}
            {actions}
          </div>
        )}
        {showMeta && (
          <span
            aria-hidden="true"
            className="text-[10px] font-mono font-medium tracking-wider text-ia-text-muted mt-1"
          >
            helpTopicId = {helpTopicId}
          </span>
        )}
      </div>
    )
  },
)
EmptyState.displayName = 'EmptyState'
